import { useEffect, useState } from 'react'
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom'
import * as customersApi from '@/api/customers'
import { useAuth } from '@/auth/AuthContext'
import { AppShell } from '@/components/AppShell'
import { CustomerLogo } from '@/components/CustomerLogo'
import { SettingsShell } from '@/components/SettingsShell'

export function Home() {
  const { user, isCustomer, isAdmin } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const [customers, setCustomers] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  // Admins use /customers for the cards page; / still redirects to ToDo / Expiring
  const showCustomerCards =
    !isAdmin || location.pathname === '/customers'

  useEffect(() => {
    if (!showCustomerCards) return undefined
    let cancelled = false
    async function load() {
      setLoading(true)
      setError('')
      try {
        const data = await customersApi.listCustomers()
        if (cancelled) return
        setCustomers(data)
        if (isCustomer && data.length === 1) {
          navigate(`/customers/${data[0].id}`, { replace: true })
        }
      } catch (err) {
        if (!cancelled) {
          setError(
            typeof err.response?.data?.detail === 'string'
              ? err.response.data.detail
              : 'Could not load customers.',
          )
        }
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    load()
    return () => {
      cancelled = true
    }
  }, [showCustomerCards, isCustomer, navigate])

  // Admins land on the ToDo / Expiring watch list by default
  if (isAdmin && !showCustomerCards) {
    return <Navigate to="/expiries" replace />
  }

  const main = (
    <>
      <h1 className="font-display text-4xl tracking-tight">Customers</h1>
      <p className="mt-2 text-brand-muted">
        {isCustomer
          ? 'Open your company knowledge base.'
          : 'Select a customer to view or edit their IT documentation.'}
      </p>
      {isAdmin && (
        <p className="mt-1 text-sm text-brand-muted">
          Administration is under Settings in the left menu. Upload logos under
          Manage customers.
        </p>
      )}

      {error && (
        <p className="mt-4 text-sm text-red-700" role="alert">
          {error}
        </p>
      )}

      {loading ? (
        <p className="mt-8 text-brand-muted">Loading…</p>
      ) : customers.length === 0 ? (
        <p className="mt-8 text-brand-muted">No customers yet.</p>
      ) : (
        <ul className="mt-8 grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
          {customers.map((c) => (
            <li key={c.id}>
              <Link
                to={`/customers/${c.id}`}
                className="flex h-full items-center gap-4 rounded-xl border border-slate-200 bg-white/90 px-4 py-4 shadow-sm transition hover:border-slate-300 hover:bg-slate-50"
              >
                <CustomerLogo
                  customerId={c.id}
                  logoFilename={c.logo_filename}
                  updatedAt={c.updated_at}
                  className="h-14 w-14"
                />
                <div className="min-w-0 flex-1">
                  <p className="truncate font-semibold text-brand">{c.name}</p>
                  {!c.is_active && (
                    <p className="text-xs text-amber-700">Inactive</p>
                  )}
                  <p className="mt-0.5 text-xs text-brand-muted">Open</p>
                </div>
              </Link>
            </li>
          ))}
        </ul>
      )}

      <p className="mt-8 text-xs text-brand-muted">
        Signed in as {user?.full_name} ({user?.role})
      </p>
    </>
  )

  if (isAdmin) {
    return <SettingsShell>{main}</SettingsShell>
  }

  return <AppShell>{main}</AppShell>
}
